import { useMemo, useState } from 'react'
import {
  ArrowRight,
  Building2,
  CheckCircle2,
  CircleDashed,
  Clock3,
  Quote,
  SendHorizontal,
  Tag,
  UserRoundCheck,
  UserRoundPlus,
  XCircle,
} from 'lucide-react'
import { Modal, Button, Badge, Select } from '@/components/ui'
import type { SelectOption } from '@/components/ui'
import { SectionLoader } from '@/components/common/SectionLoader'
import { Avatar } from '@/components/common/Avatar'
import { QueryErrorState } from '@/components/common/QueryErrorState'
import { useLeaveDetail, useLeaveDecision, useLeaveReplacement } from '@/features/leave/hooks/useLeaves'
import { usePermissions } from '@/hooks/usePermissions'
import { useMe } from '@/features/settings/hooks/useMe'
import { useCompanyMembers } from '@/features/companies/hooks/useCompanies'
import { getMemberId, getMemberLabel, getMemberSubline } from '@/features/companies/utils/memberLabel'
import { formatDate, formatDateTime, formatRelative, userSubline } from '@/lib/formatters'
import { cn } from '@/lib/cn'
import type { LeaveDocument } from '@/features/leave/types/leave.types'
import {
  canDecideLeave,
  formatLeaveLength,
  getRoleSlug,
  leaveStatusBadgeVariant,
  leaveStatusLabel,
  leaveTiming,
  leaveTypeLabel,
  parseLeaveDate,
} from './leaveUtils'

interface LeaveDetailModalProps {
  companyId: string
  leaveId: string | null
  /**
   * The list row that was opened. The detail endpoint returns the bare request
   * without requester fields, so the header reads name/email/employee id from here.
   */
  summary?: LeaveDocument | null
  /** Shown in the header; the modal has no company lookup of its own. */
  companyName?: string
  onClose: () => void
  onActionComplete?: () => void
}

export function LeaveDetailModal({
  companyId,
  leaveId,
  summary,
  companyName,
  onClose,
  onActionComplete,
}: LeaveDetailModalProps): React.ReactElement | null {
  const { data: me } = useMe()
  const { can } = usePermissions()
  const { data: detail, isLoading, error } = useLeaveDetail(companyId, leaveId)
  const decisionMutation = useLeaveDecision()
  const replacementMutation = useLeaveReplacement()

  const [showRejectForm, setShowRejectForm] = useState(false)
  const [rejectNote, setRejectNote] = useState('')
  const [showCoverForm, setShowCoverForm] = useState(false)
  const [coverUserId, setCoverUserId] = useState('')

  // Fresh detail wins for anything it carries (status, decision, cover); the
  // list row fills in the requester fields the detail endpoint leaves out.
  const leave = useMemo<LeaveDocument | undefined>(() => {
    if (!detail) return undefined
    const row = summary && summary._id === detail._id ? summary : undefined
    return { ...row, ...detail }
  }, [detail, summary])

  const canReplace = can('leave', 'assign_replacement')
  // Members resolve the cover's and reviewer's names, and feed the cover picker.
  const needsMembers =
    canReplace || Boolean(leave?.replacementUserId || leave?.decidedBy)
  const { data: membersPage } = useCompanyMembers(
    needsMembers && companyId ? companyId : null,
    { page: 1, limit: 100 },
  )

  const memberNameById = useMemo(() => {
    const map = new Map<string, string>()
    for (const m of membersPage?.data ?? []) map.set(getMemberId(m), getMemberLabel(m))
    return map
  }, [membersPage?.data])

  if (!leaveId) return null

  const roleSlug = getRoleSlug(me)
  const canApprove = can('leave', 'approve')
  const canReject = can('leave', 'reject')
  const canDecide = leave
    ? canDecideLeave(leave.status, canApprove || canReject, roleSlug)
    : false
  // Cover can still change after approval — the backend re-hands-off the shifts.
  const canAssignCover =
    canReplace && !!leave && leave.status !== 'rejected' && leave.status !== 'cancelled'
  const isDecisionPending = decisionMutation.isPending
  const requesterName =
    leave?.requesterName?.trim() || leave?.requesterEmail || 'The employee'

  const resetForms = () => {
    setShowRejectForm(false)
    setRejectNote('')
    setShowCoverForm(false)
    setCoverUserId('')
  }

  const handleClose = () => {
    resetForms()
    onClose()
  }

  const handleApprove = () => {
    decisionMutation.mutate(
      { companyId, leaveId, body: { approve: true } },
      {
        onSuccess: () => {
          handleClose()
          onActionComplete?.()
        },
      }
    )
  }

  const handleRejectSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const note = rejectNote.trim()
    if (!note) return
    decisionMutation.mutate(
      { companyId, leaveId, body: { approve: false, note } },
      {
        onSuccess: () => {
          handleClose()
          onActionComplete?.()
        },
      }
    )
  }

  const handleCoverSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!coverUserId) return
    // Stay open: the detail refetches and the cover row shows the new name.
    replacementMutation.mutate(
      { companyId, leaveId, body: { replacementUserId: coverUserId } },
      {
        onSuccess: () => {
          setShowCoverForm(false)
          setCoverUserId('')
        },
      }
    )
  }

  const memberOptions: SelectOption[] = (membersPage?.data ?? [])
    .filter((m) => getMemberId(m) !== leave?.userId)
    .map((m) => ({
      value: getMemberId(m),
      label: getMemberLabel(m),
      description: getMemberSubline(m),
    }))

  const decisionHint =
    leave?.status === 'pending_admin'
      ? 'This is the final approval.'
      : roleSlug === 'admin'
        ? 'Approving as an admin is final.'
        : 'Your company may also require an admin’s final approval.'

  const footer = canDecide ? (
    showRejectForm ? (
      <div className="flex w-full justify-end gap-2">
        <Button
          type="button"
          variant="secondary"
          onClick={() => {
            setShowRejectForm(false)
            setRejectNote('')
          }}
          disabled={isDecisionPending}
        >
          Back
        </Button>
        <Button
          type="submit"
          form="leave-reject-form"
          variant="danger"
          disabled={!rejectNote.trim()}
          loading={isDecisionPending}
        >
          Reject request
        </Button>
      </div>
    ) : (
      <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {canApprove ? decisionHint : null}
        </p>
        <div className="flex justify-end gap-2">
          {canReject && (
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowRejectForm(true)}
              disabled={isDecisionPending}
            >
              Reject
            </Button>
          )}
          {canApprove && (
            <Button
              type="button"
              variant="primary"
              onClick={handleApprove}
              loading={isDecisionPending}
              disabled={isDecisionPending}
            >
              {leave?.status === 'pending_admin' ? 'Give final approval' : 'Approve'}
            </Button>
          )}
        </div>
      </div>
    )
  ) : (
    <div className="flex w-full justify-end">
      <Button type="button" variant="secondary" onClick={handleClose}>
        Close
      </Button>
    </div>
  )

  return (
    <Modal
      open={!!leaveId}
      onClose={handleClose}
      title="Leave request"
      className="max-w-2xl"
      footer={leave ? footer : null}
    >
      {isLoading ? (
        <SectionLoader variant="detail" label="Loading leave request" />
      ) : error ? (
        <QueryErrorState error={error} size="inline" title="Couldn't load this leave request" />
      ) : leave ? (
        <div className="space-y-5">
          <RequesterHeader leave={leave} companyName={companyName} />
          <TimeOff leave={leave} />
          <ReasonBlock leave={leave} />
          <ApprovalTrack
            leave={leave}
            reviewerName={leave.decidedBy ? memberNameById.get(leave.decidedBy) : undefined}
          />
          <CoverBlock
            coverName={
              leave.replacementUserId
                ? memberNameById.get(leave.replacementUserId) ?? 'A team member'
                : undefined
            }
            canAssign={canAssignCover}
            showForm={showCoverForm}
            options={memberOptions}
            value={coverUserId}
            isSaving={replacementMutation.isPending}
            onOpenForm={() => setShowCoverForm(true)}
            onCancel={() => {
              setShowCoverForm(false)
              setCoverUserId('')
            }}
            onChange={setCoverUserId}
            onSubmit={handleCoverSubmit}
          />

          {canDecide && showRejectForm && (
            <form
              id="leave-reject-form"
              onSubmit={handleRejectSubmit}
              className="space-y-2 rounded-lg border border-red-200 bg-red-50/50 p-4 dark:border-red-500/30 dark:bg-red-500/5"
            >
              <label
                htmlFor="leave-reject-note"
                className="block text-sm font-medium text-gray-900 dark:text-gray-100"
              >
                Why are you rejecting this? <span className="text-danger">*</span>
              </label>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                {requesterName} will see this message.
              </p>
              <textarea
                id="leave-reject-note"
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                rows={3}
                required
                autoFocus
                placeholder="e.g. Not enough cover that week"
                className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              />
            </form>
          )}
        </div>
      ) : null}
    </Modal>
  )
}

const SECTION_HEADING =
  'mb-2 text-xs font-semibold uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400'

function RequesterHeader({
  leave,
  companyName,
}: {
  leave: LeaveDocument
  companyName?: string
}): React.ReactElement {
  const name = leave.requesterName?.trim() || leave.requesterEmail || 'Unknown employee'
  const email = leave.requesterEmail?.trim()
  const subline = userSubline(
    leave.requesterEmployeeId,
    email && email !== name ? email : undefined
  )

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50/70 p-4 dark:border-gray-700 dark:bg-gray-800/40">
      <div className="flex items-start gap-3">
        <Avatar name={name} size="lg" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="truncate text-base font-semibold text-gray-900 dark:text-gray-100">
              {name}
            </h3>
            <Badge variant={leaveStatusBadgeVariant(leave.status)} dot>
              {leaveStatusLabel(leave.status)}
            </Badge>
          </div>
          {subline && (
            <p className="truncate text-sm text-gray-600 dark:text-gray-400">{subline}</p>
          )}
        </div>
      </div>
      <dl className="mt-3 grid gap-2 border-t border-gray-200 pt-3 text-sm sm:grid-cols-3 dark:border-gray-700">
        <MetaItem icon={Building2} label="Company" value={companyName ?? '—'} />
        <MetaItem icon={Tag} label="Leave type" value={leaveTypeLabel(leave.leaveType)} />
        <MetaItem
          icon={SendHorizontal}
          label="Submitted"
          value={leave.createdAt ? formatRelative(leave.createdAt) : '—'}
          title={leave.createdAt ? formatDateTime(leave.createdAt) : undefined}
        />
      </dl>
    </div>
  )
}

function MetaItem({
  icon: Icon,
  label,
  value,
  title,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  value: string
  title?: string
}): React.ReactElement {
  return (
    <div className="min-w-0">
      <dt className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
        <Icon className="h-3.5 w-3.5" aria-hidden />
        {label}
      </dt>
      <dd className="truncate font-medium text-gray-900 dark:text-gray-100" title={title}>
        {value}
      </dd>
    </div>
  )
}

/** A torn-off calendar leaf: weekday, big day number, month and year. */
function DateLeaf({ date, label }: { date: Date; label: string }): React.ReactElement {
  return (
    <div className="flex min-w-0 flex-col items-center text-center">
      <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
      <div className="mt-1.5 w-20 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-[0_1px_2px_rgba(2,6,23,0.05)] dark:border-gray-700 dark:bg-gray-900">
        <div className="bg-primary/10 py-0.5 text-[11px] font-semibold text-primary dark:bg-sky-400/10 dark:text-sky-300">
          {formatDate(date, 'EEE')}
        </div>
        <div className="pt-1 text-2xl font-semibold leading-8 tabular-nums text-gray-900 dark:text-gray-100">
          {formatDate(date, 'd')}
        </div>
        <div className="pb-1.5 text-[11px] text-gray-500 dark:text-gray-400">
          {formatDate(date, 'MMM yyyy')}
        </div>
      </div>
    </div>
  )
}

const TIMING_PILL: Record<string, string> = {
  now: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300',
  soon: 'bg-amber-50 text-amber-800 dark:bg-amber-500/10 dark:text-amber-300',
  later: 'bg-gray-100 text-gray-600 dark:bg-gray-700/60 dark:text-gray-300',
  past: 'bg-gray-100 text-gray-500 dark:bg-gray-700/60 dark:text-gray-400',
}

function TimeOff({ leave }: { leave: LeaveDocument }): React.ReactElement {
  const start = parseLeaveDate(leave.startDate)
  const end = parseLeaveDate(leave.endDate)
  const singleDay = leave.startDate.slice(0, 10) === leave.endDate.slice(0, 10)
  const length = formatLeaveLength(leave)
  const timing = leaveTiming(leave)

  const summary = (
    <div className="flex flex-col items-center gap-1.5 text-center">
      <span className="text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-100">
        {length}
      </span>
      {timing && (
        <span
          className={cn(
            'rounded-full px-2 py-0.5 text-xs font-medium',
            TIMING_PILL[timing.tone]
          )}
        >
          {timing.label}
        </span>
      )}
    </div>
  )

  return (
    <section aria-labelledby="leave-timeoff-heading">
      <h4 id="leave-timeoff-heading" className={SECTION_HEADING}>
        Time off
      </h4>
      <div className="rounded-xl border border-gray-200 px-4 py-4 dark:border-gray-700">
        {singleDay ? (
          <div className="flex items-center justify-center gap-8">
            <DateLeaf date={start} label={leave.durationType === 'half' ? 'Half day' : 'Full day'} />
            {summary}
          </div>
        ) : (
          <div className="grid grid-cols-[auto_1fr_auto] items-center gap-3 sm:px-6">
            <DateLeaf date={start} label="First day" />
            <div className="flex min-w-0 flex-col items-center gap-2">
              <div className="flex w-full items-center gap-2 text-gray-300 dark:text-gray-600" aria-hidden>
                <span className="h-px flex-1 bg-current" />
                <ArrowRight className="h-3.5 w-3.5 shrink-0" />
              </div>
              {summary}
            </div>
            <DateLeaf date={end} label="Last day" />
          </div>
        )}
      </div>
    </section>
  )
}

function ReasonBlock({ leave }: { leave: LeaveDocument }): React.ReactElement {
  const reason = leave.reason?.trim()
  return (
    <section aria-labelledby="leave-reason-heading">
      <h4 id="leave-reason-heading" className={SECTION_HEADING}>
        Employee’s reason
      </h4>
      {reason ? (
        <div className="flex gap-2 rounded-lg border border-gray-200 p-3 text-sm text-gray-900 dark:border-gray-700 dark:text-gray-100">
          <Quote className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" aria-hidden />
          <p className="whitespace-pre-line">{reason}</p>
        </div>
      ) : (
        <p className="text-sm text-gray-500 dark:text-gray-400">No reason given.</p>
      )}
    </section>
  )
}

type Step = {
  key: string
  state: 'done' | 'current' | 'rejected' | 'muted'
  title: string
  detail?: string
  detailTitle?: string
  note?: string
}

/**
 * What the status can prove about the request's path. The backend overwrites
 * `decidedBy` at each step, so a finished request shows one decision — we never
 * invent a first-stage approver we can't see.
 */
function buildSteps(leave: LeaveDocument, reviewerName?: string): Step[] {
  const submitted: Step = {
    key: 'submitted',
    state: 'done',
    title: 'Submitted',
    detail: leave.createdAt ? formatRelative(leave.createdAt) : undefined,
    detailTitle: leave.createdAt ? formatDateTime(leave.createdAt) : undefined,
  }
  const by = reviewerName ? ` by ${reviewerName}` : ''
  const decided = {
    detail: leave.decidedAt ? formatDateTime(leave.decidedAt) : undefined,
  }

  switch (leave.status) {
    case 'pending':
      return [submitted, { key: 'review', state: 'current', title: 'Waiting for a manager or admin to review' }]
    case 'pending_admin':
      return [
        submitted,
        { key: 'first', state: 'done', title: `First approval${by}`, ...decided },
        { key: 'final', state: 'current', title: 'Waiting for an admin’s final approval' },
      ]
    case 'approved':
      return [submitted, { key: 'approved', state: 'done', title: `Approved${by}`, ...decided }]
    case 'rejected':
      return [
        submitted,
        {
          key: 'rejected',
          state: 'rejected',
          title: `Rejected${by}`,
          ...decided,
          note: leave.rejectionReason,
        },
      ]
    case 'cancelled':
      return [
        submitted,
        {
          key: 'cancelled',
          state: 'muted',
          title: 'Cancelled',
          detail: leave.updatedAt ? formatDateTime(leave.updatedAt) : undefined,
        },
      ]
    default:
      return [submitted]
  }
}

const STEP_ICON: Record<Step['state'], { icon: React.ComponentType<{ className?: string }>; className: string }> = {
  done: { icon: CheckCircle2, className: 'text-emerald-600 dark:text-emerald-400' },
  current: { icon: Clock3, className: 'text-amber-600 dark:text-amber-400' },
  rejected: { icon: XCircle, className: 'text-red-600 dark:text-red-400' },
  muted: { icon: CircleDashed, className: 'text-gray-400 dark:text-gray-500' },
}

function ApprovalTrack({
  leave,
  reviewerName,
}: {
  leave: LeaveDocument
  reviewerName?: string
}): React.ReactElement {
  const steps = buildSteps(leave, reviewerName)
  return (
    <section aria-labelledby="leave-track-heading">
      <h4 id="leave-track-heading" className={SECTION_HEADING}>
        Approval
      </h4>
      <ol className="space-y-0">
        {steps.map((step, index) => {
          const { icon: Icon, className } = STEP_ICON[step.state]
          const isLast = index === steps.length - 1
          return (
            <li key={step.key} className="relative flex gap-3 pb-3 last:pb-0">
              {!isLast && (
                <span
                  aria-hidden
                  className="absolute left-[7.5px] top-5 bottom-0 w-px bg-gray-200 dark:bg-gray-700"
                />
              )}
              <Icon className={cn('relative mt-0.5 h-4 w-4 shrink-0 bg-white dark:bg-gray-800', className)} aria-hidden />
              <div className="min-w-0 flex-1 text-sm">
                <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                  <p
                    className={cn(
                      'font-medium',
                      step.state === 'current'
                        ? 'text-gray-700 dark:text-gray-300'
                        : 'text-gray-900 dark:text-gray-100'
                    )}
                  >
                    {step.title}
                  </p>
                  {step.detail && (
                    <span
                      className="text-xs tabular-nums text-gray-500 dark:text-gray-400"
                      title={step.detailTitle}
                    >
                      {step.detail}
                    </span>
                  )}
                </div>
                {step.note && (
                  <p className="mt-1 whitespace-pre-line rounded-md bg-red-50/70 px-2.5 py-1.5 text-gray-800 dark:bg-red-500/10 dark:text-gray-200">
                    {step.note}
                  </p>
                )}
              </div>
            </li>
          )
        })}
      </ol>
    </section>
  )
}

function CoverBlock({
  coverName,
  canAssign,
  showForm,
  options,
  value,
  isSaving,
  onOpenForm,
  onCancel,
  onChange,
  onSubmit,
}: {
  coverName?: string
  canAssign: boolean
  showForm: boolean
  options: SelectOption[]
  value: string
  isSaving: boolean
  onOpenForm: () => void
  onCancel: () => void
  onChange: (v: string) => void
  onSubmit: (e: React.FormEvent) => void
}): React.ReactElement | null {
  // Nothing to show and nothing the viewer can do: skip the section entirely.
  if (!coverName && !canAssign) return null

  return (
    <section aria-labelledby="leave-cover-heading">
      <h4 id="leave-cover-heading" className={SECTION_HEADING}>
        Cover
      </h4>
      {showForm ? (
        <form
          onSubmit={onSubmit}
          className="space-y-3 rounded-lg border border-gray-200 p-3 dark:border-gray-700"
        >
          <label
            htmlFor="leave-cover-user"
            className="block text-sm font-medium text-gray-900 dark:text-gray-100"
          >
            Who covers their shifts?
          </label>
          <Select
            id="leave-cover-user"
            options={options}
            value={value}
            onChange={onChange}
            placeholder="Select a team member"
            searchable
          />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={isSaving}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" size="sm" disabled={!value} loading={isSaving}>
              {coverName ? 'Change cover' : 'Assign cover'}
            </Button>
          </div>
        </form>
      ) : (
        <div className="flex items-center gap-3 rounded-lg border border-gray-200 px-3 py-2.5 dark:border-gray-700">
          {coverName ? (
            <>
              <UserRoundCheck className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden />
              <p className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                {coverName}
              </p>
            </>
          ) : (
            <>
              <UserRoundPlus className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
              <p className="min-w-0 flex-1 text-sm text-gray-500 dark:text-gray-400">
                No cover assigned
              </p>
            </>
          )}
          {canAssign && (
            <Button type="button" variant="secondary" size="sm" onClick={onOpenForm}>
              {coverName ? 'Change' : 'Assign cover'}
            </Button>
          )}
        </div>
      )}
    </section>
  )
}

LeaveDetailModal.displayName = 'LeaveDetailModal'
